import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/list';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';

import { BaseComponent } from '~components/abstractions/base.component';
import { BaseSearchStore } from '~core/abstractions/base-search.store';
import { Identifyable } from '~core/abstractions/identifyable';
import { ColumnFilterModel } from '~core/models/column-filter.model';
import { OperandTypeEnum } from '~core/models/enums/operand-type.enum';
import { FilterItemModel } from '~core/models/filter-item.model';
import { NumberPipe } from '~core/pipes/number.pipe';
import { FilterService } from '~core/services/filter.service';
import { SearchRequestService } from '~core/services/search-request.service';
import { TranslationService } from '~core/services/translation.service';
import { OptionalType } from '~shared/core/types/optional.type';
import { getFilterOperator } from './filter-operators';
import { FilterPartComponent } from './filter-part/filter-part.component';
import { SaveFilterDialogComponent } from './save-filter-dialog/save-filter-dialog.component';

@Component({
    selector: 'arc-filter',
    templateUrl: './filter.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        MatCardHeader,
        MatCardTitle,
        MatProgressSpinner,
        MatIconButton,
        MatTooltip,
        MatIcon,
        MatCardContent,
        MatChipListbox,
        MatChipOption,
        MatChipRemove,
        MatDivider,
        FilterPartComponent,
        MatButton,
        TranslocoPipe,
        NumberPipe
    ]
})
export class FilterComponent<TId = number> extends BaseComponent implements OnInit {
    readonly store = input.required<BaseSearchStore<Identifyable<TId>, TId>>();

    readonly currentFilters = computed(() => this.filterService.currentFilters());
    readonly readonlyFilters = computed(() =>
        this.filterService
            .currentReadonlyFilters()
            .map(f => {
                const filterItem = this.filterItems().find(item => item.columnName === f.column);
                return [f, filterItem] as [ColumnFilterModel, OptionalType<FilterItemModel>];
            })
            .filter(([, filterItem]) => !!filterItem)
            .map(([f, filterItem]) => ({
                internalId: f.internalId,
                label: `${filterItem!.label || filterItem!.columnName} ${this.translationService.getText(
                    getFilterOperator(filterItem!.type, f.comparisonOperator)?.labelKey ?? ''
                )} ${f.values[0]}`
            }))
    );
    readonly filterItems = computed(() => this.filterService.filterItems());
    readonly selectableFilterItems = computed(() => this.filterItems().filter(item => !item.isReadOnly));

    // state
    readonly isValid = computed(() => this.filterService.isValid());
    readonly isLoading = computed(() => this.filterService.currentState().isLoading);
    readonly hasUnappliedChanges = computed(() => this.filterService.hasUnappliedChanges());
    readonly resultCount = computed(() => this.filterService.currentState().resultCount);
    readonly isSearchResultCapHit = computed(() => (this.resultCount() ?? 0) === SearchRequestService.MAX_SEARCH_RESULTS);

    // saving / loading filters
    readonly currentSavedFilter = computed(() => {
        const filter = this.filterService.currentSavedFilter();

        return !!filter
            ? {
                  ...filter,
                  isDefault: filter.name === FilterService.DEFAULT_FILTER_NAME
              }
            : undefined;
    });
    readonly hasUnsavedChanges = computed(() => this.filterService.hasUnsavedChanges());
    readonly canSave = computed(() => this.filterService.canSave());
    readonly isSaving = computed(() => this.filterService.currentState().isSaving);

    readonly canApply = computed(() => this.isValid() && this.hasUnappliedChanges());

    private readonly matDialog = inject(MatDialog);
    private readonly translationService = inject(TranslationService);
    private readonly filterService = inject(FilterService);

    constructor() {
        super();
        this.filterService.resetService();
    }

    ngOnInit(): void {
        this.filterService.initService(this.store());
    }

    applyFilters(): void {
        this.filterService.apply();
    }

    addFilter(): void {
        this.filterService.addFilter(
            new ColumnFilterModel({
                operand: OperandTypeEnum.And
            })
        );
    }

    removeReadOnlyFilter(internalId: string): void {
        this.filterService.removeReadonlyFilter(internalId);
        this.filterService.apply();
    }

    reset(): void {
        this.filterService.resetSavedFilter();
        this.filterService.resetAllFilters();
    }

    saveCurrentFilters(): void {
        const dialogRef = this.matDialog.open(SaveFilterDialogComponent);
        dialogRef.afterClosed().subscribe((result?: { name: string; isDefaultFilter: boolean }) => {
            if (!!result) {
                this.filterService.saveAndApplyCurrentFilter(result.name, result.isDefaultFilter);
            }
        });
    }

    updateCurrentSavedFilter(): void {
        this.filterService.updateSavedFilter();
    }
}
